import {
  getEQAllowedGains,
  getEQAllowedFreq,
  EQAllowedGains,
  EQAllowedFrequencies,
} from "shared/games/mixpuzzles/types";
import {
  normalizedEQFreqLookup,
  reversedNormalizedEQFreqLookup,
  reversedNormalizedEQGainLookUp,
  defaultNormalizedEQFreq,
  defaultNormalizedEQGain,
  normalizedEQGainLookUp,
} from "./constants";

export function allowedFreqAndGainToNormalizedFreqAndGain({
  eq_freq,
  eq_gain,
}: {
  eq_freq: EQAllowedFrequencies;
  eq_gain: EQAllowedGains;
}): {
  gain: number;
  freq: number;
} {
  const normalizedGain = reversedNormalizedEQGainLookUp.get(
    getEQAllowedGains(eq_gain)
  );
  const normalizedFreq = reversedNormalizedEQFreqLookup.get(
    getEQAllowedFreq(eq_freq)
  );

  return {
    gain: normalizedGain ?? defaultNormalizedEQGain,
    freq: normalizedFreq ?? defaultNormalizedEQFreq,
  };
}

function normalizedFreqAndGainToAllowedFreqAndGain({
  gain,
  freq,
}: {
  freq: number;
  gain: number;
}): { gain: EQAllowedGains; freq: EQAllowedFrequencies } {
  const eqGain = normalizedEQGainLookUp.get(gain) || 0;
  const eqFreq = normalizedEQFreqLookup.get(freq) || "bass";

  return {
    gain: eqGain,
    freq: eqFreq,
  };
}

function generateNormalizedFromCoords({
  gainResolution,
  freqResolution,
  y,
  x,
}: {
  gainResolution: number;
  freqResolution: number;
  y: number;
  x: number;
}) {
  function normalizedGainFromYCoord(y: number) {
    // returns 0 <-> (gainCount - 1 (zero based))
    // 0 === the top most part of the graph (i.e. the most DB gain)
    // gainCount - 1 === the bottom most part of the graph (i.e the lowest negative db)
    return Math.abs(Math.round((gainResolution / 2 - y) / gainResolution));
  }

  function normalizedFreqFromXCoord(x: number) {
    // returns 0 <-> (count - 1 (zero based))
    // 0 === the left most part of the graph (i.e. the lowest freq)
    // gainCount - 1 === the right most part of the graph (i.e the highest freq)
    return Math.abs(Math.round((freqResolution / 2 - x) / freqResolution));
  }

  return {
    gain: normalizedGainFromYCoord(y),
    freq: normalizedFreqFromXCoord(x),
  };
}

export function coordsToAllowedFreqAndGain({
  gainResolution,
  freqResolution,
  y,
  x,
}: {
  gainResolution: number;
  freqResolution: number;
  y: number;
  x: number;
}) {
  const { gain, freq } = generateNormalizedFromCoords({
    gainResolution,
    freqResolution,
    y,
    x,
  });

  return normalizedFreqAndGainToAllowedFreqAndGain({ gain, freq });
}

export function generateCoordsFromNormalized({
  gainResolution,
  freqResolution,
}: {
  gainResolution: number;
  freqResolution: number;
}) {
  function xCoordFromNormalizedFreq(d: number) {
    // d can be 0 <-> (count - 1 (zero based))
    // when d === 0 that means low
    // when d === count - 1 that means high
    return freqResolution / 2 + d * freqResolution;
  }

  function yCoordFromNormalizedFreq(d: number) {
    // d can be 0 <-> (count - 1 (zero based))
    // when d === 0 that means the most gain
    // when d === gainCount - 1 that means the least gain
    return gainResolution / 2 + d * gainResolution;
  }

  return {
    x: xCoordFromNormalizedFreq,
    y: yCoordFromNormalizedFreq,
  };
}
